/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AuthenticationService } from '@alfresco/adf-core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { WebscriptApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

/**
 * Per-user settings stored **on the server**, in Alfresco's own preferences.
 *
 * Used for the parts of the redesign Alfresco has no first-class concept for —
 * pinned favourites, saved searches — so that a user's choices follow them to
 * any browser or device. Browser storage was rejected for these: it would make
 * the same account behave differently on every machine, and would put state the
 * product treats as real out of the repository's reach.
 *
 * Reads and writes go through the legacy webscript
 * (`/alfresco/s/api/people/{user}/preferences`) because the public REST API
 * exposes preferences read-only. The same webscript is already used elsewhere in
 * this application for operations the public API does not cover.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferenceService {
  private readonly api = inject(AlfrescoApiService);
  private readonly authentication = inject(AuthenticationService);

  /**
   * Cached so a write does not re-fetch the person on every call — and dropped
   * the moment anyone signs in or out, because this service outlives a session:
   * held on, it would ask the repository for the previous person's preferences
   * and be answered 401.
   */
  private username?: string;

  constructor() {
    this.authentication.onLogin.subscribe(() => (this.username = undefined));
    this.authentication.onLogout.subscribe(() => (this.username = undefined));
  }

  private webscript(method: string, scriptPath: string, args?: any, body?: any): Observable<any> {
    const ws = new WebscriptApi(this.api.getInstance() as any);
    return from(ws.executeWebScript(method, scriptPath, args, undefined, undefined, body) as Promise<any>);
  }

  private currentUser(): Observable<string> {
    if (this.username) {
      return of(this.username);
    }

    const inst: any = this.api.getInstance();
    return from(
      inst.contentClient.callApi('/people/-me-', 'GET', {}, {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>
    ).pipe(
      map((res) => res?.entry?.id as string),
      tap((id) => (this.username = id))
    );
  }

  /**
   * Reads one preference.
   *
   * `key` is the dotted path Alfresco stores, e.g. `aca.favorites.pinned`.
   * A missing or unreadable preference resolves to `undefined` rather than
   * failing, so a screen never breaks because a setting has never been saved.
   */
  read<T = string>(key: string): Observable<T | undefined> {
    return this.currentUser().pipe(
      switchMap((user) => this.webscript('GET', `api/people/${encodeURIComponent(user)}/preferences`, { pf: key })),
      map((res) => key.split('.').reduce((node: any, part) => (node == null ? undefined : node[part]), res) as T | undefined),
      catchError(() => of(undefined))
    );
  }

  /** Writes one preference, creating the nested structure Alfresco expects. */
  write(key: string, value: string): Observable<void> {
    return this.currentUser().pipe(
      switchMap((user) => {
        const parts = key.split('.');
        const body: any = {};
        let node = body;
        parts.forEach((part, i) => {
          node[part] = i === parts.length - 1 ? value : {};
          node = node[part];
        });

        return this.webscript('POST', `api/people/${encodeURIComponent(user)}/preferences`, undefined, body);
      }),
      map(() => undefined)
    );
  }

  /** Reads a JSON-encoded preference, falling back when it is absent or corrupt. */
  readJson<T>(key: string, fallback: T): Observable<T> {
    return this.read<string>(key).pipe(
      map((raw) => {
        if (!raw) {
          return fallback;
        }
        try {
          return JSON.parse(raw) as T;
        } catch {
          // a hand-edited or truncated preference must not break the screen
          return fallback;
        }
      })
    );
  }

  /** Writes a value as JSON. */
  writeJson(key: string, value: unknown): Observable<void> {
    return this.write(key, JSON.stringify(value));
  }
}
